// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * 应用表面渲染策略（批次性能优化 T02）。
 *
 * 本模块是**只读派生的单一策略所有者**：由「已存在的路由权威 + 认证权威」算出当前
 * 页面属于哪一类表面，哥工作区是否允许展示型引擎（粒子光标 / Lens 光学舞台）。
 *
 * 约束（见 docs/plans/2026-10-25-agent-workspaces-performance-implementation-plan.md §3.2）：
 * 1. 策略不写进 PhysicsAgentState，不创建第二份认证或线程状态；输入只读。
 * 2. 不通过页面卸载时增减全局计数器或给 body 打补丁来判定当前表面（避免路由竞态）。
 * 3. 默认 `existing`：非目标页面（首页、登录/认证页、营销子页、设置/知识库/管理后台）
 *    行为完全不变。
 * 4. 工作区策略不可被 high-fidelity 主题选项重新打开粒子或 Lens。
 */

import { DEEP_AGENTS } from '@/config/deepAgentRegistry';
import { RESEARCH_STUDIO_SURFACE } from '@/research-workbench/productSurfaceAuthority';
import { RESEARCH_WORKBENCH_SURFACE } from '@/research-workbench/surfaceAuthority';

export type AppSurfaceKind = 'pending' | 'existing' | 'agent-workspace';

export interface AppSurfacePolicy {
  /** 表面类别：认证引导中 / 既有展示体系 / 智能体工作区。 */
  readonly kind: AppSurfaceKind;
  /** 粒子光标：`defer` 认证未完成先不挂载；`existing` 维持现有效果；`system` 使用系统鼠标。 */
  readonly cursor: 'defer' | 'existing' | 'system';
  /** Lens 光学玻璃：`defer` 认证未完成先不挂载；`existing` 维持现有效果；`off` 不创建任何光学舞台。 */
  readonly opticalGlass: 'defer' | 'existing' | 'off';
  /** 表面材质：`existing` 沿用现有 CSS 玻璃/模糊；`flat` 使用工作区静态平面令牌。 */
  readonly surfaceStyle: 'existing' | 'flat';
}

/** 写入 `<html>` 的属性名；CSS 作用域与 Portal 标记共用同一常量。 */
export const APP_SURFACE_ATTRIBUTE = 'data-app-surface';

export const PENDING_SURFACE_POLICY: AppSurfacePolicy = Object.freeze({
  kind: 'pending',
  cursor: 'defer',
  opticalGlass: 'defer',
  surfaceStyle: 'existing',
});

export const EXISTING_SURFACE_POLICY: AppSurfacePolicy = Object.freeze({
  kind: 'existing',
  cursor: 'existing',
  opticalGlass: 'existing',
  surfaceStyle: 'existing',
});

export const AGENT_WORKSPACE_SURFACE_POLICY: AppSurfacePolicy = Object.freeze({
  kind: 'agent-workspace',
  cursor: 'system',
  opticalGlass: 'off',
  surfaceStyle: 'flat',
});

/**
 * 工作区路由白名单（全部取自既有路由权威，不另建第二份智能体注册表）。
 *
 * - 主聊天：已登录 `/`
 * - 五个 DeepAgent：`DEEP_AGENTS[].path`
 * - 科研工作台 / 科研总台：两个 surfaceAuthority
 * - 附属工作区：课程/项目会话工作区、学习工作区
 * - 会话兼容入口：`/chat`、`/chat/:sessionId`（重定向期间不短暂启动展示引擎）
 */
const AGENT_WORKSPACE_EXACT_PATHS: ReadonlySet<string> = new Set<string>([
  ...DEEP_AGENTS.map((agent) => agent.path),
  RESEARCH_WORKBENCH_SURFACE.path,
  RESEARCH_STUDIO_SURFACE.path,
  '/learning',
  '/chat',
  // 2026-10-25 用户追加（T20）：设置页与知识库页及其子页签/query/浮层
  // 使用同一轻量工作区策略；仍以精确路径登记，不做字符串前缀猜测。
  '/settings',
  '/kb',
]);

/** 前缀匹配的工作区路由（动态段）。 */
const AGENT_WORKSPACE_PATH_PREFIXES: readonly string[] = ['/chat/', '/workspaces/'];

function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  const withoutQuery = pathname.split('?')[0]!.split('#')[0]!;
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.replace(/\/+$/, '') || '/';
  }
  return withoutQuery || '/';
}

/** `/` 在已登录时是主聊天工作区，未登录时是营销首页（保留现有展示体系）。 */
function isRootWorkspace(isAuthenticated: boolean): boolean {
  return isAuthenticated;
}

export interface ClassifyAppSurfacePathOptions {
  /** 认证状态只影响 `/`（已登录主聊天 vs 未登录首页）；不影响其他路由。 */
  readonly isAuthenticated: boolean;
}

/**
 * 纯路径分类：`agent-workspace` 或 `existing`。不读全局、不产生副作用。
 */
export function classifyAppSurfacePath(
  pathname: string,
  { isAuthenticated }: ClassifyAppSurfacePathOptions
): Exclude<AppSurfaceKind, 'pending'> {
  const normalized = normalizePathname(pathname);
  if (normalized === '/') {
    return isRootWorkspace(isAuthenticated) ? 'agent-workspace' : 'existing';
  }
  if (AGENT_WORKSPACE_EXACT_PATHS.has(normalized)) {
    return 'agent-workspace';
  }
  if (AGENT_WORKSPACE_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return 'agent-workspace';
  }
  return 'existing';
}

/** 该路径是否属于智能体工作区（已登录语境）。供测试与调用方断言页面范围。 */
export function isAgentWorkspacePath(pathname: string): boolean {
  return classifyAppSurfacePath(pathname, { isAuthenticated: true }) === 'agent-workspace';
}

export interface ResolveAppSurfacePolicyInput {
  readonly pathname: string;
  readonly authBootstrapDone: boolean;
  readonly isAuthenticated: boolean;
}

/**
 * 认证引导未完成时一律 `pending`：先不挂粒子/光学引擎，避免「短暂启动后又卸载」，
 * 也避免工作区曾启动装饰引擎（plan §3.2 约束 3）。
 */
export function resolveAppSurfacePolicy({
  pathname,
  authBootstrapDone,
  isAuthenticated,
}: ResolveAppSurfacePolicyInput): AppSurfacePolicy {
  if (!authBootstrapDone) {
    return PENDING_SURFACE_POLICY;
  }
  const kind = classifyAppSurfacePath(pathname, { isAuthenticated });
  return kind === 'agent-workspace' ? AGENT_WORKSPACE_SURFACE_POLICY : EXISTING_SURFACE_POLICY;
}

/** 工作区策略下是否允许粒子光标挂载（`defer` 也不允许，等策略落定）。 */
export function shouldMountCursor(policy: AppSurfacePolicy): boolean {
  return policy.cursor === 'existing';
}

/** 工作区策略下是否允许创建 Lens 光学舞台（`defer` 也不允许）。 */
export function shouldMountOpticalGlass(policy: AppSurfacePolicy): boolean {
  return policy.opticalGlass === 'existing';
}
